import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { fetchWrapper } from '../../helpers';
import { getSerializedErrorMessage } from '../../utils/errors';
import { FIELD_TEMPLATES_API_BASE } from './constants';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type {
  FieldTemplatesState,
  GetFieldTemplatePayload,
  GetFieldTemplatesPayload,
  IFieldTemplate,
  IFieldTemplateDetail,
  UpdateFieldTemplatePayload,
} from './types';

export type { FieldTemplatesState, IFieldTemplate, IFieldTemplateDetail } from './types';

const initialState = createInitialState();
const extraActions = createExtraActions();
const slice = createSlice({
  name: 'fieldTemplates',
  initialState,
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<FieldTemplatesState>) =>
    createExtraReducers(builder),
});

function createInitialState(): FieldTemplatesState {
  return {
    fields: [],
    fieldsLoading: false,
    fieldTemplate: null,
    fieldTemplateLoading: false,
    requestedTemplateIdentifier: null,
    updateFieldTemplateLoading: false,
    error: null,
  };
}

function createExtraActions() {
  return {
    getFields: getFields(),
    getByIdentifier: getByIdentifier(),
    updateByIdentifier: updateByIdentifier(),
  };
}

function getFields() {
  return createAsyncThunk<IFieldTemplate[], GetFieldTemplatesPayload>(
    'fieldTemplates/getFields',
    async ({ resource, withTemplate, getAccessTokenSilently }) => {
      const params = new URLSearchParams({
        with_template: String(withTemplate),
      });
      if (resource) {
        params.set('resource', resource);
      }
      const response: IFieldTemplate[] = await fetchWrapper.get({
        url: `${FIELD_TEMPLATES_API_BASE}/fields/?${params}`,
        getAccessTokenSilently,
      });
      return response;
    },
  );
}

function getByIdentifier() {
  return createAsyncThunk<IFieldTemplateDetail, GetFieldTemplatePayload>(
    'fieldTemplates/getByIdentifier',
    async ({ identifier, getAccessTokenSilently }) => {
      const response: IFieldTemplateDetail = await fetchWrapper.get({
        url: `${FIELD_TEMPLATES_API_BASE}/${identifier}/`,
        getAccessTokenSilently,
      });
      return response;
    },
  );
}

function updateByIdentifier() {
  return createAsyncThunk<IFieldTemplateDetail, UpdateFieldTemplatePayload>(
    'fieldTemplates/updateByIdentifier',
    async ({ identifier, data, getAccessTokenSilently }) => {
      const response: IFieldTemplateDetail = await fetchWrapper.put(
        {
          url: `${FIELD_TEMPLATES_API_BASE}/${identifier}/`,
          getAccessTokenSilently,
        },
        data,
      );
      return response;
    },
  );
}

function createExtraReducers(builder: ActionReducerMapBuilder<FieldTemplatesState>) {
  addGetFieldsCases(builder);
  addGetByIdentifierCases(builder);
  addUpdateByIdentifierCases(builder);
}

function addGetFieldsCases(builder: ActionReducerMapBuilder<FieldTemplatesState>) {
  const { getFields } = extraActions;

  builder
    .addCase(getFields.pending, (state: FieldTemplatesState) => {
      state.fieldsLoading = true;
      state.error = null;
    })
    .addCase(
      getFields.fulfilled,
      (state: FieldTemplatesState, action: PayloadAction<IFieldTemplate[]>) => {
        state.fieldsLoading = false;
        state.fields = action.payload;
      },
    )
    .addCase(getFields.rejected, (state: FieldTemplatesState, action) => {
      state.fieldsLoading = false;
      state.fields = [];
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addGetByIdentifierCases(builder: ActionReducerMapBuilder<FieldTemplatesState>) {
  const { getByIdentifier } = extraActions;

  builder
    .addCase(getByIdentifier.pending, (state: FieldTemplatesState, action) => {
      state.fieldTemplateLoading = true;
      state.requestedTemplateIdentifier = action.meta.arg.identifier;
      state.error = null;
    })
    .addCase(getByIdentifier.fulfilled, (state: FieldTemplatesState, action) => {
      // Ignore responses for an identifier that is no longer the latest request,
      // so a slow request that resolves out of order can't overwrite a newer one.
      if (action.meta.arg.identifier !== state.requestedTemplateIdentifier) {
        return;
      }
      state.fieldTemplateLoading = false;
      state.fieldTemplate = action.payload;
    })
    .addCase(getByIdentifier.rejected, (state: FieldTemplatesState, action) => {
      if (action.meta.arg.identifier !== state.requestedTemplateIdentifier) {
        return;
      }
      state.fieldTemplateLoading = false;
      state.fieldTemplate = null;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

function addUpdateByIdentifierCases(builder: ActionReducerMapBuilder<FieldTemplatesState>) {
  const { updateByIdentifier } = extraActions;

  builder
    .addCase(updateByIdentifier.pending, (state: FieldTemplatesState) => {
      state.updateFieldTemplateLoading = true;
      state.error = null;
    })
    .addCase(updateByIdentifier.fulfilled, (state: FieldTemplatesState, action) => {
      state.updateFieldTemplateLoading = false;
      // Only adopt the saved template if the user is still viewing that field;
      // a save that resolves after the user switched fields must not overwrite it.
      if (action.meta.arg.identifier === state.requestedTemplateIdentifier) {
        state.fieldTemplate = action.payload;
      }
    })
    .addCase(updateByIdentifier.rejected, (state: FieldTemplatesState, action) => {
      state.updateFieldTemplateLoading = false;
      state.error = action.error;
      toast.error(getSerializedErrorMessage(action.error));
    });
}

export const fieldTemplatesActions = { ...slice.actions, ...extraActions };
export const fieldTemplatesReducer = slice.reducer;
